/**
 * Written content for directory pages, generated only from stored facts.
 *
 * Every sentence here is backed by a value in the database (or a count or
 * distance computed from those values). Listings come from two sources, and
 * the wording always says which: the Companies House register (ch: rows, whose
 * address is the registered office) and OpenStreetMap (osm: rows). Wording
 * varies by a hash of the slug so thousands of company pages do not read
 * identically. Safe to import anywhere.
 */

import type { Listing, RegionCount, TownSummary, TradeCount } from "@/lib/construction/types";
import type { Region } from "@/lib/construction/places";

import { countCompanies, formatCount, hashString, listJoin, websiteHost } from "@/lib/construction/normalise";
import { getRegion, REGIONS } from "@/lib/construction/places";
import { getTrade } from "@/lib/construction/trades";
import { sicLabel } from "@/lib/construction/sic";
import { withArticle } from "@/lib/text";

export type Faq = { q: string; a: string };

const pick = <T,>(options: T[], seed: number, salt = 0): T => options[(seed + salt * 7) % options.length];

const DATE = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "long", year: "numeric", timeZone: "UTC" });

export function formatDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : DATE.format(d);
}

export function formatMoney(gbp: number): string {
  if (gbp >= 1e9) return `£${(gbp / 1e9).toFixed(2).replace(/\.?0+$/, "")} billion`;
  if (gbp >= 1e6) return `£${(gbp / 1e6).toFixed(1).replace(/\.0$/, "")} million`;
  return `£${formatCount(gbp)}`;
}

/** "active" -> "Active", "voluntary-arrangement" -> "Voluntary arrangement". */
export function formatStatus(status: string | null | undefined): string | null {
  if (!status) return null;
  const text = status.replace(/[-_]/g, " ").trim().toLowerCase();
  return text ? text.charAt(0).toUpperCase() + text.slice(1) : null;
}

/** Register company type as a noun phrase: "a private limited company". */
export function companyTypePhrase(type: string | null | undefined): string {
  const t = (type ?? "").toLowerCase();
  if (!t) return "a registered company";
  if (t.includes("limited liability partnership")) return "a limited liability partnership";
  if (t.includes("public limited")) return "a public limited company";
  if (t.includes("community interest")) return "a community interest company";
  if (t.includes("guarant")) return "a private company limited by guarantee";
  if (t.includes("unlimited")) return "a private unlimited company";
  if (t.includes("private limited")) return "a private limited company";
  if (t.includes("limited partnership")) return "a limited partnership";
  if (t.includes("society")) return "a registered society";
  return "a registered company";
}

/** "a roofer and builder", "an electrician". */
export function tradesPhrase(slugs: string[]): string {
  const nouns = slugs.map((s) => getTrade(s)?.singular).filter((n): n is string => Boolean(n));
  if (nouns.length === 0) return "a construction company";
  return withArticle(listJoin(nouns.slice(0, 3)));
}

function tradeCountsPhrase(trades: TradeCount[], n: number): string {
  return listJoin(
    trades
      .slice(0, n)
      .map((t) => {
        const trade = getTrade(t.slug);
        return trade ? `${trade.plural} (${formatCount(t.count)})` : null;
      })
      .filter((x): x is string => Boolean(x)),
  );
}

export function fullAddress(l: Pick<Listing, "address" | "postcode">): string | null {
  const parts = [l.address, l.postcode].filter(Boolean);
  return parts.length ? parts.join(", ") : null;
}

const SOURCES = "the Companies House register and OpenStreetMap";

/* ------------------------------------------------------------- Directory */

export function directoryAnswer(totals: { listings: number; towns: number }, regions: RegionCount[], trades: TradeCount[]): string {
  if (totals.listings === 0) {
    return `The ConstructionWebDesign.uk directory of UK construction companies is being built right now from ${SOURCES}. Listings for builders, roofers, electricians, plumbers and other trades appear here as each part is imported, with every town and region counted.`;
  }
  const top = [...regions].sort((a, b) => b.count - a.count)[0];
  const topRegion = top ? getRegion(top.slug) : null;
  return [
    `The ConstructionWebDesign.uk directory lists ${countCompanies(totals.listings)} in ${formatCount(totals.towns)} UK towns, from ${SOURCES}.`,
    topRegion && top ? `${topRegion.name} has the most listings (${formatCount(top.count)}).` : "",
    trades.length >= 3 ? `The most common trades are ${tradeCountsPhrase(trades, 3)}.` : "",
    "Search by company, town, postcode or trade, or browse by region.",
  ]
    .filter(Boolean)
    .join(" ");
}

export function directoryFaqs(totals: { listings: number; towns: number }, regions: RegionCount[], towns: TownSummary[], trades: TradeCount[]): Faq[] {
  const faqs: Faq[] = [];
  const sortedRegions = [...regions].sort((a, b) => b.count - a.count).filter((r) => r.count > 0);
  if (totals.listings > 0) {
    faqs.push({
      q: "How many construction companies are listed in the UK directory?",
      a: `The ConstructionWebDesign.uk directory lists ${countCompanies(totals.listings)} across ${formatCount(totals.towns)} towns in England, Scotland, Wales and Northern Ireland. Each listing is one company in one town: an active construction company on the Companies House register, or a business mapped on OpenStreetMap.`,
    });
  }
  if (sortedRegions.length >= 3) {
    const top = sortedRegions.slice(0, 3).map((r) => `${getRegion(r.slug)?.name} (${formatCount(r.count)})`);
    faqs.push({
      q: "Which UK regions have the most construction companies?",
      a: `The UK regions with the most construction companies in this directory are ${listJoin(top)}. The counts cover registered companies and mapped businesses, so sole traders who are not on the register appear only when they are mapped on OpenStreetMap.`,
    });
  }
  if (towns.length >= 3) {
    const top = towns.slice(0, 3).map((t) => `${t.name} (${formatCount(t.count)})`);
    faqs.push({
      q: "Which UK towns have the most construction companies?",
      a: `The towns with the most construction companies listed in this directory are ${listJoin(top)}. Each company is placed in the city or town nearest its registered office or map location, so a firm on the edge of a city may appear under a neighbouring town.`,
    });
  }
  if (trades.length >= 3) {
    faqs.push({
      q: "Which construction trades are most common in the UK directory?",
      a: `The most common trades in the directory are ${tradeCountsPhrase(trades, 4)}. A company's trade comes from its OpenStreetMap tag first, then its Companies House SIC codes, then the words in its name.`,
    });
  }
  faqs.push({
    q: "Where does the construction company data come from?",
    a: "The directory combines two sources. The Companies House register supplies every active construction company (SIC codes 41 to 43) that is not filing dormant accounts, placed by its registered office postcode. OpenStreetMap, © OpenStreetMap contributors under the Open Database Licence, supplies mapped businesses with their phone numbers and websites. Both refresh automatically about every 30 days, with no reviews, ratings or estimated figures.",
  });
  faqs.push({
    q: "How do I add or correct my construction company?",
    a: "Register details such as the company name, registered office and SIC codes come from Companies House, so update them there and the next monthly refresh picks up the change. Phone numbers, websites and trading addresses come from OpenStreetMap, which anyone can edit at openstreetmap.org. To remove a listing from this directory, email hello@constructionwebdesign.uk.",
  });
  return faqs;
}

/* ----------------------------------------------------------------- Region */

export function regionAnswer(region: Region, count: number, towns: TownSummary[], trades: TradeCount[]): string {
  if (count === 0) {
    return `There are no construction companies listed in ${region.inName} yet. The ConstructionWebDesign.uk directory is built from ${SOURCES} and fills in as each part is imported, so listings for ${region.longName} will appear here, counted town by town.`;
  }
  return [
    `There ${count === 1 ? "is" : "are"} ${countCompanies(count)} listed in ${region.inName}, across ${formatCount(towns.length)} ${towns.length === 1 ? "town" : "towns"}.`,
    towns[0] ? `${towns[0].name} has the most (${formatCount(towns[0].count)})${towns[1] ? `, followed by ${towns[1].name} (${formatCount(towns[1].count)})` : ""}.` : "",
    trades.length >= 2 ? `The most common trades are ${tradeCountsPhrase(trades, 2)}.` : "",
    `Every listing comes from ${SOURCES}.`,
  ]
    .filter(Boolean)
    .join(" ");
}

export function regionFaqs(region: Region, count: number, towns: TownSummary[], trades: TradeCount[]): Faq[] {
  if (count === 0) return [];
  const faqs: Faq[] = [
    {
      q: `How many construction companies are in ${region.inName}?`,
      a: `The ConstructionWebDesign.uk directory lists ${countCompanies(count)} in ${region.inName}, spread across ${formatCount(towns.length)} ${towns.length === 1 ? "town" : "towns"}. The figure counts active construction companies on the Companies House register and businesses mapped on OpenStreetMap.`,
    },
  ];
  if (towns.length >= 2) {
    faqs.push({
      q: `Which towns in ${region.inName} have the most construction companies?`,
      a: `The towns in ${region.inName} with the most construction companies in this directory are ${listJoin(towns.slice(0, 5).map((t) => `${t.name} (${formatCount(t.count)})`))}. Each company is placed in the city or town nearest its registered office or map location.`,
    });
  }
  const top = trades[0] ? getTrade(trades[0].slug) : null;
  if (top && trades[0]) {
    faqs.push({
      q: `How many ${top.plural} are in ${region.inName}?`,
      a: `There ${trades[0].count === 1 ? "is" : "are"} ${formatCount(trades[0].count)} ${trades[0].count === 1 ? top.singular : top.plural} listed in ${region.inName}, the most common trade in the region's ${countCompanies(count)}. A company can appear under more than one trade.`,
    });
  }
  if (trades.length >= 3) {
    faqs.push({
      q: `Which construction trades are most common in ${region.inName}?`,
      a: `The most common construction trades in ${region.inName} are ${tradeCountsPhrase(trades, 4)}. Trades come from each company's OpenStreetMap tag, then its Companies House SIC codes, then the words in its name.`,
    });
  }
  faqs.push(enquiriesFaq(region.inName));
  return faqs;
}

/* ------------------------------------------------------------------- Town */

export function townAnswer(town: TownSummary, trades: TradeCount[], nearby: TownSummary[]): string {
  const region = getRegion(town.region);
  return [
    `There ${town.count === 1 ? "is" : "are"} ${countCompanies(town.count)} listed in ${town.name}, ${region?.longName ?? "UK"}.`,
    trades.length >= 2 ? `The most common trades are ${tradeCountsPhrase(trades, 3)}.` : trades[0] ? `All are listed as ${getTrade(trades[0].slug)?.plural ?? "construction companies"}.` : "",
    nearby.length >= 2 ? `Nearby towns with listings include ${nearby[0].name} and ${nearby[1].name}.` : "",
    "Each listing shows the details recorded at Companies House or on OpenStreetMap.",
  ]
    .filter(Boolean)
    .join(" ");
}

export function townFaqs(town: TownSummary, trades: TradeCount[], nearby: (TownSummary & { distanceKm?: number })[]): Faq[] {
  const region = getRegion(town.region);
  const faqs: Faq[] = [
    {
      q: `How many construction companies are in ${town.name}?`,
      a: `The ConstructionWebDesign.uk directory lists ${countCompanies(town.count)} in ${town.name}, ${region?.longName ?? "UK"}. Companies are placed in the city or town nearest their registered office or map location, so the list also covers firms in the surrounding area.`,
    },
  ];
  const builders = trades.find((t) => t.slug === "builders") ?? trades[0];
  const bt = builders ? getTrade(builders.slug) : null;
  if (builders && bt) {
    faqs.push({
      q: `How many ${bt.plural} are in ${town.name}?`,
      a: `There ${builders.count === 1 ? "is" : "are"} ${formatCount(builders.count)} ${builders.count === 1 ? bt.singular : bt.plural} listed in ${town.name}, out of ${countCompanies(town.count)} in the town. A company can be listed under more than one trade when its SIC codes, OpenStreetMap entry or name show it.`,
    });
  }
  if (trades.length >= 2) {
    faqs.push({
      q: `Which construction trades are most common in ${town.name}?`,
      a: `The most common construction trades in ${town.name} are ${tradeCountsPhrase(trades, 4)}. Trades come from each company's OpenStreetMap tag, then Companies House SIC codes, then the words in its name.`,
    });
  }
  if (nearby.length >= 2) {
    faqs.push({
      q: `Which towns near ${town.name} have construction companies?`,
      a: `Towns near ${town.name} with construction companies in this directory include ${listJoin(
        nearby.slice(0, 4).map((t) => `${t.name} (${formatCount(t.count)}${typeof t.distanceKm === "number" ? `, about ${Math.round(t.distanceKm)} km away` : ""})`),
      )}.`,
    });
  }
  faqs.push(enquiriesFaq(town.name));
  return faqs;
}

function enquiriesFaq(place: string): Faq {
  return {
    q: `How can a construction company in ${place} get more enquiries?`,
    a: `A construction company in ${place} wins more local enquiries when customers can find a clear website with its services, projects and the areas it covers. ConstructionWebDesign.uk builds websites for construction companies and trades for a fixed £1,000, live in 7 days, with on-page local SEO included and no monthly fees.`,
  };
}

/* ---------------------------------------------------------------- Company */

export type CompanyContext = {
  townCount: number;
  nearest: { name: string; town: string; distanceKm: number }[];
  townLat: number | null;
  townLng: number | null;
};

export type CompanyContent = {
  lede: string;
  about: string[];
  tradesSentence: string;
  companiesHouse: {
    number: string;
    status: string | null;
    incorporated: string | null;
    sicCodes: string[];
    sicLabels: string[];
    companyType: string | null;
    registeredName: string | null;
  } | null;
  ranking: { sentence: string; sourceName: string | null; sourceUrl: string | null } | null;
  faqs: Faq[];
};

function kmBetween(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const a = Math.sin(toRad(lat2 - lat1) / 2) ** 2 + Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(toRad(lng2 - lng1) / 2) ** 2;
  return 6371 * 2 * Math.asin(Math.sqrt(a));
}

function rankingContent(l: Listing): CompanyContent["ranking"] {
  if (!l.ranking) return null;
  const r = l.ranking;
  const who = r.legalName ?? r.name;
  const parts = [l.source === "ch" ? `This company is ${r.name}, matched by its Companies House number.` : `This listing is matched by name to ${r.name}.`];
  if (r.revenueGbp !== null && r.revenuePeriod) parts.push(`${who} reported revenue of ${formatMoney(r.revenueGbp)} for the ${r.revenuePeriod.replace(/^Year/, "year")}.`);
  if (r.employees !== null) parts.push(`It reported ${formatCount(r.employees)} employees${r.employeesPeriod ? ` (${r.employeesPeriod.replace(/^Year/, "year")})` : ""}.`);
  return { sentence: parts.join(" "), sourceName: r.sourceName, sourceUrl: r.sourceUrl };
}

function townCountSentence(l: Listing, ctx: CompanyContext, seed: number): string {
  return ctx.townCount <= 1
    ? `${l.name} is the only construction company listed in ${l.town} in this directory so far.`
    : pick(
        [
          `${l.name} is one of ${countCompanies(ctx.townCount)} listed in ${l.town} in this directory.`,
          `This directory lists ${countCompanies(ctx.townCount)} in ${l.town}, including ${l.name}.`,
          `${l.name} appears alongside ${formatCount(ctx.townCount - 1)} other construction ${ctx.townCount - 1 === 1 ? "company" : "companies"} listed in ${l.town}.`,
        ],
        seed,
        2,
      );
}

function nearestFaq(l: Listing, ctx: CompanyContext, seed: number): Faq | null {
  if (ctx.nearest.length < 2) return null;
  return {
    q: pick([`Which construction companies are near ${l.name}?`, `What other construction companies are close to ${l.name}?`], seed, 7),
    a: `Construction companies listed near ${l.name} include ${listJoin(
      ctx.nearest.slice(0, 3).map((n) => `${n.name} in ${n.town} (about ${n.distanceKm < 1 ? "under 1" : Math.round(n.distanceKm)} km away)`),
    )}.`,
  };
}

/** A company from the Companies House register: the address is its registered office. */
function registerCompanyContent(l: Listing, ctx: CompanyContext): CompanyContent {
  const seed = hashString(l.slug);
  const region = getRegion(l.region) ?? REGIONS[0];
  const trades = tradesPhrase(l.trades);
  const address = fullAddress(l);
  const number = l.companiesHouseNumber ?? l.sourceKey.replace(/^ch:/, "");
  const incorporated = formatDate(l.chIncorporatedOn);
  const typeNoun = companyTypePhrase(l.chCompanyType);
  const sicLabels = l.sicCodes.map(sicLabel);

  const lede = [
    pick(
      [
        `${l.name} is ${trades} registered with Companies House, with its registered office in ${l.town}, ${region.longName}.`,
        `${l.name} is ${trades} whose registered office is in ${l.town}, ${region.inName}.`,
        `With its registered office in ${l.town}, ${l.name} is ${trades} on the Companies House register.`,
      ],
      seed,
    ),
    address
      ? pick([`Its registered office address is ${address}.`, `The registered office on record is ${address}.`, `Companies House gives its registered office as ${address}.`], seed, 1)
      : "",
  ]
    .filter(Boolean)
    .join(" ");

  const about: string[] = [`${l.name} is ${typeNoun}${incorporated ? `, incorporated on ${incorporated}` : ""}, with company number ${number}.`];
  if (sicLabels.length) {
    about.push(
      pick(
        [
          `Companies House lists its business ${sicLabels.length === 1 ? "activity as SIC code" : "activities as SIC codes"} ${listJoin(sicLabels)}.`,
          `Its registered ${sicLabels.length === 1 ? "SIC code is" : "SIC codes are"} ${listJoin(sicLabels)}.`,
        ],
        seed,
        5,
      ),
    );
  }
  about.push(townCountSentence(l, ctx, seed));
  if (l.lat !== null && l.lng !== null && ctx.townLat !== null && ctx.townLng !== null) {
    const km = kmBetween(l.lat, l.lng, ctx.townLat, ctx.townLng);
    about.push(
      km < 1
        ? `Its registered office postcode is within a kilometre of the centre of ${l.town}.`
        : `Its registered office postcode is about ${Math.round(km)} km from the centre of ${l.town}.`,
    );
  }
  if (l.employees !== null) {
    about.push(`${l.name} has ${formatCount(l.employees)} employees, according to details added to this directory.`);
  }

  const tradeNouns = l.trades.map((s) => getTrade(s)?.plural).filter((p): p is string => Boolean(p));
  const tradesSentence = tradeNouns.length
    ? `${l.name} is listed under ${tradeNouns.length === 1 ? "one trade" : `${tradeNouns.length} trades`}: ${listJoin(tradeNouns)}, based on its SIC codes${l.sicCodes.length ? "" : " and name"}.`
    : `${l.name} is listed as a construction company.`;

  const status = formatStatus(l.chStatus) ?? "Active";
  const faqs: Faq[] = [
    {
      q: pick([`Where is ${l.name} based?`, `Where is ${l.name}'s registered office?`, `What is the address of ${l.name}?`], seed, 4),
      a: [
        address ? `${l.name}'s registered office is at ${address}, in ${l.town}, ${region.longName}.` : `${l.name}'s registered office is in ${l.town}, ${region.longName}.`,
        "A registered office is the official address on the Companies House register, which is not always where a company carries out its work.",
      ].join(" "),
    },
    {
      q: pick([`What does ${l.name} do?`, `What kind of company is ${l.name}?`], seed, 5),
      a: sicLabels.length
        ? `Companies House lists ${l.name} under SIC ${sicLabels.length === 1 ? "code" : "codes"} ${listJoin(sicLabels)}, so this directory lists it as ${trades}.`
        : `This directory lists ${l.name} as ${trades}.`,
    },
  ];
  if (incorporated) {
    faqs.push({
      q: `When was ${l.name} incorporated?`,
      a: `${l.name} was incorporated on ${incorporated}, according to the Companies House register.`,
    });
  }
  const host = websiteHost(l.website);
  if (host) {
    faqs.push({
      q: pick([`Does ${l.name} have a website?`, `What is the website of ${l.name}?`], seed, 6),
      a: `Yes. ${l.name}'s website is ${host}. ConstructionWebDesign.uk matched it because the site shows the company number ${number}, which UK companies must display on their websites.`,
    });
  }
  faqs.push({
    q: `Is ${l.name} registered with Companies House?`,
    a: `Yes. ${l.name} is ${typeNoun} on the Companies House register, company number ${number}, with the status "${status}". Source: Companies House.`,
  });
  const near = nearestFaq(l, ctx, seed);
  if (near) faqs.push(near);

  return {
    lede,
    about,
    tradesSentence,
    companiesHouse: {
      number,
      status,
      incorporated,
      sicCodes: l.sicCodes,
      sicLabels,
      companyType: l.chCompanyType,
      registeredName: l.chName && l.chName !== l.name ? l.chName : null,
    },
    ranking: rankingContent(l),
    faqs,
  };
}

/** An OpenStreetMap business, with Companies House details when they were matched. */
function mappedCompanyContent(l: Listing, ctx: CompanyContext): CompanyContent {
  const seed = hashString(l.slug);
  const region = getRegion(l.region) ?? REGIONS[0];
  const trades = tradesPhrase(l.trades);
  const address = fullAddress(l);
  const host = websiteHost(l.website);

  const lede = [
    pick(
      [
        `${l.name} is ${trades} in ${l.town}, ${region.longName}.`,
        `${l.name} is listed in OpenStreetMap as ${trades} based in ${l.town}.`,
        `Based in ${l.town}, ${l.name} is ${trades} in ${region.inName}.`,
      ],
      seed,
    ),
    address ? pick([`Its listed address is ${address}.`, `The address on record is ${address}.`, `The recorded address is ${address}.`], seed, 1) : "",
  ]
    .filter(Boolean)
    .join(" ");

  const about: string[] = [townCountSentence(l, ctx, seed)];
  if (l.lat !== null && l.lng !== null && ctx.townLat !== null && ctx.townLng !== null) {
    const km = kmBetween(l.lat, l.lng, ctx.townLat, ctx.townLng);
    about.push(
      km < 1
        ? `It is mapped within a kilometre of the centre of ${l.town}.`
        : pick([`It is mapped about ${Math.round(km)} km from the centre of ${l.town}.`, `Its map location is roughly ${Math.round(km)} km from central ${l.town}.`], seed, 3),
    );
  }
  if (l.locations > 1) {
    about.push(`OpenStreetMap holds ${formatCount(l.locations)} locations for ${l.name} in ${l.town}.`);
  }
  if (l.employees !== null) {
    about.push(`${l.name} has ${formatCount(l.employees)} employees, according to details added to this directory.`);
  }

  const tradeNouns = l.trades.map((s) => getTrade(s)?.plural).filter((p): p is string => Boolean(p));
  const tradesSentence = tradeNouns.length
    ? `${l.name} is listed under ${tradeNouns.length === 1 ? "one trade" : `${tradeNouns.length} trades`}: ${listJoin(tradeNouns)}.`
    : `${l.name} is listed as a construction company.`;

  const companiesHouse = l.companiesHouseNumber
    ? {
        number: l.companiesHouseNumber,
        status: formatStatus(l.chStatus),
        incorporated: formatDate(l.chIncorporatedOn),
        sicCodes: l.sicCodes,
        sicLabels: l.sicCodes.map(sicLabel),
        companyType: l.chCompanyType,
        registeredName: l.chName && l.chName !== l.name ? l.chName : null,
      }
    : null;

  const faqs: Faq[] = [];
  faqs.push({
    q: pick([`Where is ${l.name} based?`, `Where is ${l.name}?`, `What is the address of ${l.name}?`], seed, 4),
    a: [
      `${l.name} is based in ${l.town}, ${region.longName}.`,
      address ? `Its address in OpenStreetMap is ${address}.` : "OpenStreetMap does not record a street address for it, but its map location is shown on this page.",
    ].join(" "),
  });
  faqs.push({
    q: pick([`What does ${l.name} do?`, `What kind of company is ${l.name}?`], seed, 5),
    a: `${l.name} is listed as ${trades}. ${tradeNouns.length > 1 ? `It appears under ${listJoin(tradeNouns)} in this directory.` : ""} The trade comes from its OpenStreetMap entry, its Companies House SIC codes where available, or its name.`.replace(/\s+/g, " ").trim(),
  });
  if (l.phone || l.website) {
    const how = [l.phone ? `by phone on ${l.phone}` : "", host ? `through its website, ${host}` : ""].filter(Boolean);
    faqs.push({
      q: pick([`How do I contact ${l.name}?`, `What are the contact details for ${l.name}?`], seed, 6),
      a: `You can contact ${l.name} ${listJoin(how)}. These contact details come from OpenStreetMap, so check them on the company's own website before relying on them.`,
    });
  }
  if (companiesHouse) {
    faqs.push({
      q: `Is ${l.name} registered with Companies House?`,
      a: [
        `Yes. Companies House lists company number ${companiesHouse.number} for ${l.name}`,
        companiesHouse.status ? `, with the status "${companiesHouse.status}"` : "",
        companiesHouse.incorporated ? `, incorporated on ${companiesHouse.incorporated}` : "",
        ". Source: Companies House.",
      ].join(""),
    });
  }
  const near = nearestFaq(l, ctx, seed);
  if (near) faqs.push(near);

  return { lede, about, tradesSentence, companiesHouse, ranking: rankingContent(l), faqs };
}

export function buildCompanyContent(l: Listing, ctx: CompanyContext): CompanyContent {
  return l.source === "ch" ? registerCompanyContent(l, ctx) : mappedCompanyContent(l, ctx);
}
